import { Html5Qrcode } from "html5-qrcode";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, ButtonBase, CircularProgress, InputBase, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import useParticipantes from "../hooks/useParticipantes";
import { CORES, combinaBusca, ehPresente, horaCurta } from "../utils/participante";
import { AvatarIniciais, Pagina } from "./ui/Painel";
import { DATA_EVENTO } from "../config/evento";

const READER_ID = "leitor-qr-video";
const TEMPO_RESULTADO_MS = 8000;
const TEMPO_MESMO_CODIGO_MS = 4000;
const CHAVE_CHECKINS = `checkins_${DATA_EVENTO}`;

const varredura = keyframes`
  0%   { top: 8%;  opacity: .2; }
  50%  { opacity: 1; }
  100% { top: 88%; opacity: .2; }
`;

const cartao = {
  backgroundColor: "#fff",
  border: `1px solid ${CORES.borda}`,
  borderRadius: "10px",
};

const botaoCamera = {
  height: 32,
  px: 1.5,
  borderRadius: "6px",
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "inherit",
  color: "rgba(255,255,255,0.85)",
  border: "1px solid rgba(255,255,255,0.16)",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
  "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" },
};

const Canto = ({ posicao }) => {
  const lados = {
    tl: { top: 0, left: 0, borderTop: 3, borderLeft: 3, borderTopLeftRadius: 12 },
    tr: { top: 0, right: 0, borderTop: 3, borderRight: 3, borderTopRightRadius: 12 },
    bl: { bottom: 0, left: 0, borderBottom: 3, borderLeft: 3, borderBottomLeftRadius: 12 },
    br: { bottom: 0, right: 0, borderBottom: 3, borderRight: 3, borderBottomRightRadius: 12 },
  };
  return (
    <Box
      sx={{
        position: "absolute",
        width: 30,
        height: 30,
        borderColor: "#fff",
        borderStyle: "solid",
        borderWidth: 0,
        ...lados[posicao],
      }}
    />
  );
};

// Painel da direita com o resultado da ultima leitura.
const Resultado = ({ resultado }) => {
  if (!resultado) {
    return (
      <Box
        sx={{
          ...cartao,
          border: `1.5px dashed #cdd5df`,
          minHeight: 140,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 3,
          py: 3.5,
        }}
      >
        <CenterFocusStrongOutlinedIcon sx={{ fontSize: 28, color: "#6b7a8a", mb: 1 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: CORES.texto }}>Aguardando leitura</Typography>
        <Typography sx={{ fontSize: 12.5, color: CORES.textoSuave, mt: 0.5, maxWidth: 260 }}>
          Aponte a câmera para o QR Code do convite. O resultado aparece aqui.
        </Typography>
      </Box>
    );
  }

  if (resultado.tipo === "processando") {
    return (
      <Box sx={{ ...cartao, minHeight: 140, display: "grid", placeItems: "center" }}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={28} sx={{ color: CORES.navy }} />
          <Typography sx={{ fontSize: 13, color: CORES.textoSuave, mt: 1.5 }}>Validando convite…</Typography>
        </Box>
      </Box>
    );
  }

  const estilos = {
    sucesso: { cor: CORES.verde, fundo: "#f2faf5", borda: "#9fd5b4", Icone: CheckCircleRoundedIcon },
    aviso: { cor: "#9a6a00", fundo: "#fffaf0", borda: "#f0d48f", Icone: InfoRoundedIcon },
    erro: { cor: CORES.vermelho, fundo: "#fdf5f4", borda: "#f1b8b1", Icone: ErrorRoundedIcon },
  }[resultado.tipo];
  const { Icone } = estilos;

  return (
    <Box
      sx={{
        ...cartao,
        borderColor: estilos.borda,
        backgroundColor: estilos.fundo,
        px: 3,
        py: 3,
        textAlign: "center",
      }}
    >
      <Icone sx={{ fontSize: 36, color: estilos.cor }} />
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.8, color: estilos.cor, textTransform: "uppercase", mt: 0.5 }}>
        {resultado.titulo}
      </Typography>
      {resultado.nome && (
        <Typography
          sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 800, color: CORES.texto, lineHeight: 1.15, mt: 1, wordBreak: "break-word" }}
        >
          {resultado.nome}
        </Typography>
      )}
      {resultado.tipo !== "erro" &&
        (resultado.mesa ? (
          <Box sx={{ display: "inline-block", mt: 2, px: 3.5, py: 1, borderRadius: "8px", backgroundColor: CORES.navy }}>
            <Typography sx={{ color: "rgba(255,255,255,0.75)", fontWeight: 700, fontSize: 11, letterSpacing: 1.5 }}>MESA</Typography>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: 32, sm: 38 }, lineHeight: 1 }}>{resultado.mesa}</Typography>
          </Box>
        ) : (
          <Typography sx={{ mt: 1.5, fontSize: 13, color: CORES.textoSuave }}>Mesa não definida</Typography>
        ))}
      {resultado.detalhe && (
        <Typography sx={{ fontSize: 12.5, color: CORES.textoSuave, mt: 1.5 }}>{resultado.detalhe}</Typography>
      )}
    </Box>
  );
};

const LeitorQrCode = () => {
  const { participantes, registrarEntrada } = useParticipantes();
  const [estadoCamera, setEstadoCamera] = useState("iniciando"); // iniciando | ativa | erro
  const [resultado, setResultado] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [lanternaSuportada, setLanternaSuportada] = useState(false);
  const [lanternaLigada, setLanternaLigada] = useState(false);
  const [busca, setBusca] = useState("");
  const [termoBuscado, setTermoBuscado] = useState("");
  const [registrandoId, setRegistrandoId] = useState(null);

  const scannerRef = useRef(null);
  const processandoRef = useRef(false);
  const ultimaLeituraRef = useRef({ codigo: "", em: 0 });
  const limparResultadoRef = useRef(null);
  const participantesRef = useRef(participantes);
  participantesRef.current = participantes;

  // Ultimos check-ins feitos NESTE aparelho (o banco nao guarda horario de
  // entrada) — fica no navegador pra sobreviver a um F5 durante o evento.
  const [checkinsLocais, setCheckinsLocais] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CHAVE_CHECKINS) || "[]");
    } catch {
      return [];
    }
  });

  const anotarCheckin = useCallback((nome, mesa) => {
    setCheckinsLocais((prev) => {
      const nova = [{ nome, mesa, em: new Date().toISOString() }, ...prev].slice(0, 30);
      try {
        localStorage.setItem(CHAVE_CHECKINS, JSON.stringify(nova));
      } catch (_) {}
      return nova;
    });
  }, []);

  const mostrarResultado = useCallback((novo) => {
    setResultado(novo);
    if (limparResultadoRef.current) clearTimeout(limparResultadoRef.current);
    if (novo && novo.tipo !== "processando") {
      limparResultadoRef.current = setTimeout(() => setResultado(null), TEMPO_RESULTADO_MS);
    }
  }, []);

  const pararScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (!scanner) return;
    try {
      if (scanner.isScanning) await scanner.stop();
      scanner.clear();
    } catch (error) {
      console.warn("Erro ao parar scanner:", error);
    }
  }, []);

  const processarLeitura = useCallback(
    async (textoLido) => {
      const codigo = String(textoLido).split("/").pop().trim();
      const agora = Date.now();
      if (processandoRef.current) return;
      if (ultimaLeituraRef.current.codigo === codigo && agora - ultimaLeituraRef.current.em < TEMPO_MESMO_CODIGO_MS) {
        return;
      }
      ultimaLeituraRef.current = { codigo, em: agora };
      processandoRef.current = true;

      // Ja esta na lista como presente? Avisa sem chamar o backend.
      const conhecido = participantesRef.current.find(
        (p) => String(p.codigo_convite || "").toUpperCase() === codigo.toUpperCase()
      );
      if (conhecido && ehPresente(conhecido)) {
        mostrarResultado({
          tipo: "aviso",
          titulo: "Entrada já registrada",
          nome: conhecido.nome,
          mesa: conhecido.mesa,
          detalhe: "Esse convidado já passou pela recepção.",
        });
        processandoRef.current = false;
        return;
      }

      mostrarResultado({ tipo: "processando" });
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/participantes/confirmar-presenca/${encodeURIComponent(codigo)}`
        );
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          mostrarResultado({
            tipo: "erro",
            titulo: response.status === 404 ? "Convite não encontrado" : "Erro na leitura",
            detalhe:
              response.status === 404
                ? "QR Code inválido, excluído ou com entrada já registrada."
                : "Não foi possível confirmar agora. Tente novamente.",
          });
          return;
        }

        if (navigator.vibrate) navigator.vibrate(120);
        anotarCheckin(data.nome, data.mesa);
        mostrarResultado({
          tipo: "sucesso",
          titulo: "Entrada liberada",
          nome: data.nome,
          mesa: data.mesa,
          detalhe: `Check-in às ${horaCurta(new Date())}`,
        });
      } catch (error) {
        console.error("Erro ao confirmar presença:", error);
        mostrarResultado({
          tipo: "erro",
          titulo: "Sem conexão",
          detalhe: "Verifique a internet e leia o QR Code novamente.",
        });
      } finally {
        processandoRef.current = false;
      }
    },
    [mostrarResultado, anotarCheckin]
  );

  const iniciarScanner = useCallback(
    async (camera = { facingMode: "environment" }) => {
      setEstadoCamera("iniciando");
      setLanternaSuportada(false);
      setLanternaLigada(false);
      await pararScanner();

      const scanner = new Html5Qrcode(READER_ID, { verbose: false });
      scannerRef.current = scanner;

      try {
        await scanner.start(camera, { fps: 12 }, (texto) => processarLeitura(texto), () => {});
        if (scannerRef.current !== scanner) return;
        setEstadoCamera("ativa");

        try {
          const lanterna = scanner.getRunningTrackCameraCapabilities().torchFeature();
          setLanternaSuportada(lanterna.isSupported());
        } catch (_) {
          setLanternaSuportada(false);
        }

        Html5Qrcode.getCameras()
          .then((lista) => setCameras(lista || []))
          .catch(() => setCameras([]));
      } catch (error) {
        console.error("Erro ao iniciar câmera:", error);
        if (scannerRef.current === scanner) {
          scannerRef.current = null;
          setEstadoCamera("erro");
        }
      }
    },
    [pararScanner, processarLeitura]
  );

  useEffect(() => {
    const timer = setTimeout(() => iniciarScanner(), 300);
    return () => {
      clearTimeout(timer);
      if (limparResultadoRef.current) clearTimeout(limparResultadoRef.current);
      pararScanner();
    };
  }, [iniciarScanner, pararScanner]);

  const trocarCamera = () => {
    const scanner = scannerRef.current;
    if (cameras.length < 2) return;
    let atual = -1;
    try {
      const { deviceId } = scanner.getRunningTrackSettings();
      atual = cameras.findIndex((c) => c.id === deviceId);
    } catch (_) {}
    const proxima = cameras[(atual + 1) % cameras.length];
    iniciarScanner({ deviceId: { exact: proxima.id } });
  };

  const alternarLanterna = async () => {
    try {
      const lanterna = scannerRef.current.getRunningTrackCameraCapabilities().torchFeature();
      await lanterna.apply(!lanternaLigada);
      setLanternaLigada(!lanternaLigada);
    } catch (error) {
      console.warn("Lanterna indisponível:", error);
      setLanternaSuportada(false);
    }
  };

  const resultadosBusca = useMemo(() => {
    if (!termoBuscado.trim()) return [];
    return participantes
      .filter((p) => combinaBusca(p, "", termoBuscado))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
      .slice(0, 6);
  }, [participantes, termoBuscado]);

  const registrarPorBusca = async (p) => {
    setRegistrandoId(p.id);
    try {
      await registrarEntrada(p.id);
      anotarCheckin(p.nome, p.mesa);
      mostrarResultado({
        tipo: "sucesso",
        titulo: "Entrada liberada",
        nome: p.nome,
        mesa: p.mesa,
        detalhe: `Check-in manual às ${horaCurta(new Date())}`,
      });
    } catch (error) {
      mostrarResultado({
        tipo: "erro",
        titulo: "Não foi possível registrar",
        nome: p.nome,
        detalhe: error.response?.data?.error || "Tente novamente.",
      });
    } finally {
      setRegistrandoId(null);
    }
  };

  const presentes = participantes.filter(ehPresente);
  const ultimosCheckins = checkinsLocais.slice(0, 6);

  return (
    <Pagina
      eyebrow="Recepção"
      titulo="Check-in por QR Code"
      acoes={
        <Box sx={{ ...cartao, display: "flex", alignItems: "baseline", gap: 1.5, px: 2, py: 1 }}>
          <Typography sx={{ fontSize: 11.5, color: CORES.textoSuave }}>Presentes</Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: CORES.navy, lineHeight: 1 }}>
            {presentes.length}
            <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: "#8a97a6" }}>
              {" "}/ {participantes.length}
            </Box>
          </Typography>
        </Box>
      }
    >
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5, alignItems: "start" }}>
          {/* Camera */}
          <Box
            sx={{
              position: "relative",
              borderRadius: "12px",
              overflow: "hidden",
              background: "radial-gradient(circle at 50% 35%, #1d3349 0%, #0d1824 70%)",
              aspectRatio: { xs: "3 / 4", sm: "7 / 6" },
              minHeight: 380,
            }}
          >
            <Box
              id={READER_ID}
              sx={{
                position: "absolute",
                inset: 0,
                "& video": {
                  width: "100% !important",
                  height: "100% !important",
                  objectFit: "cover",
                },
                "& canvas, & img": { display: "none" },
              }}
            />

            {/* Moldura + escurecimento fora dela */}
            <Box
              sx={{
                position: "absolute",
                top: "calc(50% - 30px)",
                left: "50%",
                width: "min(52%, 260px)",
                aspectRatio: "1 / 1",
                transform: "translate(-50%, -50%)",
                borderRadius: "14px",
                boxShadow: "0 0 0 9999px rgba(8, 16, 26, 0.45)",
                pointerEvents: "none",
              }}
            >
              <Canto posicao="tl" />
              <Canto posicao="tr" />
              <Canto posicao="bl" />
              <Canto posicao="br" />
              {estadoCamera === "ativa" && (
                <Box
                  sx={{
                    position: "absolute",
                    left: "8%",
                    right: "8%",
                    height: "2px",
                    borderRadius: 1,
                    backgroundColor: "#fff",
                    boxShadow: "0 0 12px 2px rgba(255,255,255,0.6)",
                    animation: `${varredura} 2.2s ease-in-out infinite alternate`,
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                py: 0.5,
                borderRadius: "6px",
                backgroundColor: "rgba(0,0,0,0.45)",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: estadoCamera === "ativa" ? "#ff4d4f" : estadoCamera === "erro" ? "#8a97a6" : "#f2c14e",
                }}
              />
              {estadoCamera === "ativa" ? "Câmera ativa" : estadoCamera === "erro" ? "Câmera indisponível" : "Iniciando câmera"}
            </Box>

            {estadoCamera === "iniciando" && (
              <Box sx={{ position: "absolute", top: "calc(50% - 30px)", left: "50%", transform: "translate(-50%, -50%)" }}>
                <CircularProgress size={30} sx={{ color: "rgba(255,255,255,0.8)" }} />
              </Box>
            )}

            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                px: 1.5,
                pb: 1.5,
                pt: 4,
                background: "linear-gradient(to top, rgba(8,16,26,0.9), rgba(8,16,26,0))",
              }}
            >
              {estadoCamera === "erro" ? (
                <Box sx={{ textAlign: "center", mb: 1.5 }}>
                  <Typography sx={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>
                    Não foi possível abrir a câmera.
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: 12, mt: 0.5, mb: 1.5 }}>
                    Verifique a permissão do navegador. Enquanto isso, use a busca pelo nome ao lado.
                  </Typography>
                  <Button
                    onClick={() => iniciarScanner()}
                    sx={{ ...botaoCamera, color: "#fff", backgroundColor: "rgba(255,255,255,0.12)", transition: "none" }}
                  >
                    Tentar novamente
                  </Button>
                </Box>
              ) : (
                <Typography sx={{ textAlign: "center", color: "#fff", fontSize: 13, fontWeight: 500, mb: 2 }}>
                  Posicione o QR Code dentro da moldura
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ButtonBase sx={botaoCamera} disabled={estadoCamera !== "ativa" || cameras.length < 2} onClick={trocarCamera}>
                  Trocar câmera
                </ButtonBase>
                <ButtonBase
                  sx={{
                    ...botaoCamera,
                    ...(lanternaLigada && { backgroundColor: "rgba(255,255,255,0.9)", color: CORES.navy }),
                  }}
                  disabled={estadoCamera !== "ativa" || !lanternaSuportada}
                  onClick={alternarLanterna}
                >
                  Lanterna
                </ButtonBase>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>Leitura automática</Typography>
              </Box>
            </Box>
          </Box>

          {/* Coluna da direita */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Resultado resultado={resultado} />

            <Box sx={{ ...cartao, p: 1.75 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: CORES.texto, mb: 1 }}>
                Sem QR Code? Busque pelo nome
              </Typography>
              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  setTermoBuscado(busca);
                }}
                sx={{ display: "flex", gap: 1 }}
              >
                <InputBase
                  value={busca}
                  onChange={(e) => {
                    setBusca(e.target.value);
                    if (!e.target.value.trim()) setTermoBuscado("");
                  }}
                  placeholder="Nome ou telefone do convidado"
                  inputProps={{ "aria-label": "Nome ou telefone do convidado" }}
                  sx={{
                    flex: 1,
                    height: 34,
                    px: 1.25,
                    fontSize: 12.5,
                    border: `1px solid ${CORES.borda}`,
                    borderRadius: "6px",
                    "&.Mui-focused": { borderColor: CORES.navyClaro },
                  }}
                />
                <Button
                  type="submit"
                  disableElevation
                  sx={{
                    height: 34,
                    px: 2,
                    py: 0,
                    fontSize: 12.5,
                    fontWeight: 600,
                    borderRadius: "6px",
                    color: "#fff",
                    background: CORES.navyClaro,
                    transition: "none",
                    "&:hover": { background: CORES.navy, transform: "none" },
                  }}
                >
                  Buscar
                </Button>
              </Box>

              {termoBuscado.trim() && (
                <Box sx={{ mt: 1.25 }}>
                  {resultadosBusca.length === 0 ? (
                    <Typography sx={{ fontSize: 12, color: CORES.textoSuave, py: 1 }}>
                      Nenhum convidado encontrado para “{termoBuscado}”.
                    </Typography>
                  ) : (
                    resultadosBusca.map((p) => (
                      <Box
                        key={p.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          py: 1,
                          borderTop: `1px solid ${CORES.borda}`,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: CORES.texto }} noWrap>
                            {p.nome}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: CORES.textoSuave }}>
                            {p.mesa ? `Mesa ${p.mesa}` : "Sem mesa"}
                          </Typography>
                        </Box>
                        {ehPresente(p) ? (
                          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: CORES.verde, whiteSpace: "nowrap" }}>
                            ✓ Presente
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            onClick={() => registrarPorBusca(p)}
                            disabled={registrandoId === p.id}
                            sx={{
                              height: 28,
                              px: 1.25,
                              py: 0,
                              fontSize: 11.5,
                              fontWeight: 600,
                              color: CORES.navyClaro,
                              border: `1px solid ${CORES.borda}`,
                              borderRadius: "5px",
                              whiteSpace: "nowrap",
                              transition: "none",
                              "&:hover": { backgroundColor: "#f3f6f9", transform: "none" },
                            }}
                          >
                            {registrandoId === p.id ? "Registrando..." : "Registrar entrada"}
                          </Button>
                        )}
                      </Box>
                    ))
                  )}
                </Box>
              )}
            </Box>

            <Box sx={{ ...cartao, overflow: "hidden" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: CORES.texto, px: 1.75, py: 1.5, borderBottom: `1px solid ${CORES.borda}` }}>
                Últimos check-ins neste aparelho
              </Typography>
              {ultimosCheckins.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: CORES.textoSuave, px: 1.75, py: 2 }}>
                  Nenhum check-in feito neste aparelho ainda.
                </Typography>
              ) : (
                ultimosCheckins.map((p, i) => (
                  <Box
                    key={`${p.em}-${i}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 1.75,
                      py: 1.1,
                      borderTop: i === 0 ? "none" : `1px solid ${CORES.borda}`,
                    }}
                  >
                    <AvatarIniciais nome={p.nome} tom="verde" tamanho={26} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: CORES.texto }} noWrap>
                        {p.nome}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: CORES.textoSuave }}>
                        {p.mesa ? `Mesa ${p.mesa}` : "Sem mesa"}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: CORES.textoSuave, fontVariantNumeric: "tabular-nums" }}>
                      {horaCurta(p.em)}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
    </Pagina>
  );
};

export default LeitorQrCode;
